import { makeStyles } from '@mui/styles';
import TextField from 'apollo-react/components/TextField';
import TreeItem from 'apollo-react/components/TreeItem';
import TreeView from 'apollo-react/components/TreeView';
import Close from 'apollo-react-icons/Close';
import SearchIcon from 'apollo-react-icons/Search';
import _ from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { DEBOUNCE_SEARCH_TIME } from '../../../constants/Common';
import { INPUT_ICON_DEFAULT_SIZE } from '../../../constants/CommonStylesConstants';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import { findSingleField, getExpandedNodeIds } from '../../../form-designer/utils/editor.utils';
import { getUITA } from '../../../utils/commonUtils';
import { MAPPING_SOURCES } from '../../constants/report-designer.constants';
import {
  getReportModelEntityDefinition,
  getReportModelEntityHierarchy,
} from '../../selectors/report-designer.selectors';
import { _find, renderNodeTree } from '../../utils/report-designer.utils';

const renderTree = ({ nodeId, label, childNodes }) => (
  <TreeItem
    key={nodeId}
    nodeId={nodeId}
    label={label}
    ui-ta={getUITA('ReportModelTreeItem', label)}
  >
    {Array.isArray(childNodes) ? childNodes.map((node) => renderTree(node)) : null}
  </TreeItem>
);

const useStyles = makeStyles({
  mainContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    maxHeight: ({ maxHeight }) => maxHeight || 'unset',
    maxWidth: ({ maxWidth }) => maxWidth || 'unset',
  },
  treeContainer: {
    border: '1px solid #ccc',
    overflow: 'auto',
    backgroundColor: 'white',
    height: '100%',
  },
  searchInput: {
    marginTop: 0,
    '& div': {
      marginTop: 0,
    },
  },
  clearIcon: INPUT_ICON_DEFAULT_SIZE,
});

const Search = ({ handleSearch, clearSearch, handleSelectField }) => {
  const classes = useStyles();
  const searchInput = useRef();
  const translateString = useTranslation();

  const [value, setValue] = useState('');

  useEffect(() => {
    searchInput.current.focus();
  }, []);

  const handleChange = ({ target: { value } }) => {
    const newSearchText = value.length === 1 ? value.trim() : value;

    setValue(newSearchText);
    handleSearch(newSearchText);
  };

  const clearSearchText = () => {
    setValue('');
    searchInput.current.focus();

    clearSearch();
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && value) {
      handleSelectField();
    }
  };

  return (
    <TextField
      fullWidth
      ui-ta="DataModelSearch"
      startIcon={<SearchIcon />}
      icon={value !== '' ? <Close className={classes.clearIcon} /> : null}
      iconProps={{ onClick: clearSearchText }}
      placeholder={translateString('Search')}
      name="Search"
      value={value ?? ''}
      onChange={handleChange}
      onKeyDown={handleKeyPress}
      inputRef={searchInput}
      className={classes.searchInput}
    />
  );
};

const ReportModel = ({ change, singleSelect, showSearch = false, width, height }) => {
  const classes = useStyles({ width, height });
  const translateString = useTranslation();

  const reportModelEntityHierarchy = useSelector(getReportModelEntityHierarchy);
  const reportModelEntityDefinition = useSelector(getReportModelEntityDefinition);

  const [click, setClick] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [expanded, setExpanded] = useState([]);

  const canSelectRef = useRef(true);

  const dataTree = useMemo(
    () => [renderNodeTree(reportModelEntityHierarchy, reportModelEntityDefinition, searchText)],
    [reportModelEntityHierarchy, reportModelEntityDefinition, searchText]
  );

  const defaultEl = dataTree[0].nodeId;
  const showReportModelTree = !!dataTree[0].childNodes?.length;

  useEffect(() => {
    const expandedNodeIds = searchText ? getExpandedNodeIds(dataTree) : [defaultEl];
    setExpanded(expandedNodeIds);
  }, [dataTree, defaultEl, searchText]);

  const handleSelect = (e, nodeId) => {
    e.stopPropagation();

    if (!canSelectRef.current) {
      return;
    }

    setClick((prev) => prev + 1);

    setTimeout(function() {
      canSelectRef.current = true;
      setClick(0);
    }, 200);

    const nodeValue = _find(dataTree, 'nodeId', nodeId[0]);

    handleSelectAction({ nodeValue, changeFn: click > 0 ? change : null });
  };

  const handleSelectAction = ({ nodeValue, changeFn }) => {
    if (!changeFn || nodeValue.label === 'Fields' || nodeValue.childNodes?.length) {
      return;
    }

    canSelectRef.current = false;

    singleSelect
      ? changeFn({
          [MAPPING_SOURCES.ENTITY_NAME]: nodeValue.parentNodeId,
          [MAPPING_SOURCES.ENTITY_FIELD]: nodeValue.nodeId,
          [MAPPING_SOURCES.EXPRESSION_ENTITY_FIELD]: nodeValue.value,
          [MAPPING_SOURCES.TRANSLATED_ENTITY_FIELD]: nodeValue.translatedValue,
        })
      : changeFn((prev) => `${prev}${nodeValue.value}`);
  };

  const handleExpandedNodes = (_, nodeIds) => setExpanded(nodeIds);

  const clearSearch = () => {
    setSearchText('');
  };

  const debouncedHandleSearch = useMemo(
    () => _.debounce((value) => setSearchText(value), DEBOUNCE_SEARCH_TIME),
    []
  );

  const handleSelectField = () => {
    const selectedField = !!searchText && findSingleField(dataTree);

    selectedField && handleSelectAction({ nodeValue: selectedField, changeFn: change });
  };

  return (
    <div className={classes.mainContainer}>
      {!singleSelect && <h4>{translateString('Report Model')}</h4>}
      {showSearch && (
        <Search
          handleSearch={debouncedHandleSearch}
          clearSearch={clearSearch}
          handleSelectField={handleSelectField}
        />
      )}
      <div className={classes.treeContainer} ui-ta={getUITA('ReportModel', 'section')}>
        {showReportModelTree && (
          <TreeView
            variant="folder"
            defaultExpanded={[defaultEl]}
            defaultSelected={['1']}
            onNodeSelect={handleSelect}
            onNodeToggle={handleExpandedNodes}
            expanded={expanded}
            multiSelect
          >
            {dataTree.map((node) => renderTree(node))}
          </TreeView>
        )}
      </div>
    </div>
  );
};

export default React.memo(ReportModel);
